import React, { useEffect, useState } from 'react';
import { Typography, List, Tag, Badge, Button, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getNewsArticles, getNewsArticleById } from '../../content/news';
import useAuthContext from '../../hooks/useAuthContext';
import { markSeen, isUnread, newsUserId, NEWS_SEEN_EVENT } from '../../utils/newsSeen';
import ArticleSections from './ArticleSections';
import style from './index.module.css';

const { Title, Text, Paragraph } = Typography;

const formatDate = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function NewsList({ articles, userId, onSelect, onMarkSeen }) {
    if (!articles.length) {
        return <Empty description="No announcements yet" />;
    }

    return (
        <List
            className={style.list}
            dataSource={articles}
            renderItem={(article) => {
                const unread = isUnread(userId, article.id);
                return (
                    <List.Item className={style.listItem} onClick={() => onSelect(article.id)}>
                        <div className={style.listItemContent}>
                            <div className={style.listItemHeader}>
                                <Text strong className={style.listTitle}>
                                    {article.title}
                                </Text>
                                {unread ? <Badge status="processing" text="New" /> : null}
                                {unread ? (
                                    <Button
                                        size="small"
                                        className={style.markSeenBtn}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onMarkSeen(article.id);
                                        }}
                                    >
                                        Mark as seen
                                    </Button>
                                ) : null}
                            </div>
                            <Paragraph type="secondary" className={style.summary} ellipsis={{ rows: 2 }}>
                                {article.summary}
                            </Paragraph>
                            <div className={style.meta}>
                                <Text type="secondary">{formatDate(article.publishedAt)}</Text>
                                {article.tags?.map((tag) => (
                                    <Tag key={tag}>{tag}</Tag>
                                ))}
                            </div>
                        </div>
                    </List.Item>
                );
            }}
        />
    );
}

function NewsDetail({ article, onBack }) {
    return (
        <div className={style.detail}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} className={style.backBtn}>
                Back to all updates
            </Button>
            <Title level={3}>{article.title}</Title>
            <div className={style.detailMeta}>
                <Text type="secondary">{formatDate(article.publishedAt)}</Text>
                {article.tags?.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                ))}
            </div>
            <Paragraph className={style.detailSummary}>{article.summary}</Paragraph>
            <ArticleSections sections={article.sections} />
        </div>
    );
}

export default function NewsBulletin() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const userId = newsUserId(user);
    const articles = getNewsArticles();
    const article = id ? getNewsArticleById(id) : null;
    const [, setSeenTick] = useState(0);

    useEffect(() => {
        const onSeen = () => setSeenTick((tick) => tick + 1);
        window.addEventListener(NEWS_SEEN_EVENT, onSeen);
        return () => window.removeEventListener(NEWS_SEEN_EVENT, onSeen);
    }, []);

    useEffect(() => {
        if (article && userId) {
            markSeen(userId, article.id);
        }
    }, [article, userId]);

    if (id && !article) {
        return (
            <div className={style.page}>
                <Empty description="Article not found">
                    <Button type="primary" onClick={() => navigate('/news')}>
                        Back to What's New
                    </Button>
                </Empty>
            </div>
        );
    }

    return (
        <div className={style.page}>
            <Title level={2} className={style.pageTitle}>
                {id ? "What's New" : "What's New in Pegasus"}
            </Title>
            {!id ? (
                <NewsList
                    articles={articles}
                    userId={userId}
                    onSelect={(articleId) => navigate(`/news/${articleId}`)}
                    onMarkSeen={(articleId) => markSeen(userId, articleId)}
                />
            ) : (
                <NewsDetail article={article} onBack={() => navigate('/news')} />
            )}
        </div>
    );
}
